import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { ShoppingCart } from 'lucide-react'
import {
  Card,
  Chip,
  EmptyState,
  ErrorState,
  FilterToolbar,
  LoadMore,
  MoneyValue,
  SearchField,
  SelectField,
  Skeleton,
} from '@shared/design-system/ui'
import { useDebounce } from '@shared/lib/use-debounce'
import { adminListPeriodRange, dashboardRangeLabel } from '../lib/admin-dashboard-period'
import { AdminDashboardFilters } from './AdminDashboardFilters'
import type { DashboardSelection } from './AdminDashboardFilters'
import {
  useRecuperaAdminBreakdown,
  useRecuperaAdminOverview,
  useRecuperaAdminProducts,
} from '../api/admin-recupera'
import type { RecuperaAdminFilter } from '../api/admin-recupera'
import {
  cardsGrid,
  filtersRow,
  header,
  numeric,
  page,
  statCard,
  statLabel,
  statValue,
  subtitle,
  table,
  tableWrap,
  td,
  th,
  title,
} from './admin-recupera.css'

function DebouncedSearch({
  value,
  onCommit,
}: {
  value: string
  onCommit: (value: string) => void
}) {
  const [text, setText] = useState(value)
  const debounced = useDebounce(text, 300)

  useEffect(() => {
    onCommit(debounced.trim())
  }, [debounced, onCommit])

  return (
    <SearchField
      value={text}
      onChange={setText}
      placeholder="Buscar por cliente ou produto..."
      ariaLabel="Buscar recuperações"
    />
  )
}

export function AdminRecuperaScreen() {
  const [selection, setSelection] = useState<DashboardSelection>({ period: 'today' })
  const [search, setSearch] = useState('')
  const [productId, setProductId] = useState<string | undefined>()

  const range = useMemo(
    () =>
      selection.period === 'custom'
        ? (selection.custom ?? {})
        : adminListPeriodRange(selection.period),
    [selection],
  )

  const filter: RecuperaAdminFilter = {
    ...range,
    ...(selection.user?.id ? { producerId: selection.user.id } : {}),
    ...(productId ? { productId } : {}),
    ...(search ? { search } : {}),
  }

  const overview = useRecuperaAdminOverview(filter)
  const breakdown = useRecuperaAdminBreakdown(filter)

  const items = useMemo(
    () => breakdown.data?.pages.flatMap((pageData) => pageData.items) ?? [],
    [breakdown.data?.pages],
  )

  const productOptionsFilter: RecuperaAdminFilter = {
    ...range,
    ...(selection.user?.id ? { producerId: selection.user.id } : {}),
    ...(search ? { search } : {}),
  }
  const productsQuery = useRecuperaAdminProducts(productOptionsFilter)

  const productOptions = useMemo(
    () => [
      { value: '', label: 'Todos os produtos' },
      ...(productsQuery.data ?? []).map((product) => ({
        value: product.productId,
        label: product.productName ?? product.productId,
      })),
    ],
    [productsQuery.data],
  )

  const filtersActive = Boolean(search) || Boolean(productId) || Boolean(selection.user?.id)

  const chips: ReactNode[] = []
  if (selection.user?.id) {
    chips.push(
      <Chip
        key="user"
        label={`Cliente: ${selection.user.name || selection.user.email}`}
        onRemove={() => setSelection({ ...selection, user: undefined })}
      />,
    )
  }
  if (productId) {
    const label = productOptions.find((option) => option.value === productId)?.label ?? productId
    chips.push(
      <Chip key="product" label={`Produto: ${label}`} onRemove={() => setProductId(undefined)} />,
    )
  }
  if (search) {
    chips.push(<Chip key="search" label={`Busca: ${search}`} onRemove={() => setSearch('')} />)
  }

  return (
    <div className={page} data-testid="admin-recupera-screen">
      <div className={header}>
        <div>
          <h1 className={title}>Recupera AI</h1>
          <p className={subtitle}>
            Valor recuperado e comissão do Recupera AI (custo VendePay), por cliente e produto.
          </p>
        </div>
      </div>

      <AdminDashboardFilters selection={selection} onApply={setSelection} />
      <p className={subtitle}>
        Recuperações no período: {dashboardRangeLabel(range)}.{' '}
        {selection.user
          ? `Cliente: ${selection.user.name || selection.user.email}`
          : 'Todos os clientes'}
        .
      </p>

      {overview.isPending ? (
        <Skeleton width="100%" height={110} />
      ) : overview.isError ? (
        <ErrorState onRetry={() => void overview.refetch()} />
      ) : overview.data !== undefined ? (
        <div className={cardsGrid} data-testid="admin-recupera-cards">
          <div className={statCard}>
            <span className={statLabel}>Recuperados</span>
            <span className={statValue}>{overview.data.recoveredCount}</span>
          </div>
          <div className={statCard}>
            <span className={statLabel}>Valor recuperado</span>
            <MoneyValue amount={overview.data.recoveredRevenue} currency="BRL" size="lg" />
          </div>
          <div className={statCard}>
            <span className={statLabel}>Comissão Recupera AI</span>
            <MoneyValue amount={overview.data.commission} currency="BRL" size="lg" />
          </div>
        </div>
      ) : null}

      <Card>
        <FilterToolbar
          chips={chips.length > 0 ? chips : null}
          onClear={
            filtersActive
              ? () => {
                  setSearch('')
                  setProductId(undefined)
                  setSelection({ ...selection, user: undefined })
                }
              : undefined
          }
          resultCount={
            <span className={subtitle}>
              {items.length} {items.length === 1 ? 'linha carregada' : 'linhas carregadas'}
            </span>
          }
        >
          <div className={filtersRow} role="group" aria-label="Filtrar recuperações">
            <SelectField
              aria-label="Produto"
              value={productId ?? ''}
              options={productOptions}
              onChange={(event) =>
                setProductId(event.target.value === '' ? undefined : event.target.value)
              }
            />
            <DebouncedSearch key={search} value={search} onCommit={setSearch} />
          </div>
        </FilterToolbar>

        {breakdown.isPending ? (
          <Skeleton width="100%" height={240} />
        ) : breakdown.isError ? (
          <ErrorState onRetry={() => void breakdown.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ShoppingCart size={23} />}
            title="Nenhuma recuperação no período"
            description="Não há vendas recuperadas pelo Recupera AI para os filtros selecionados."
          />
        ) : (
          <>
            <div className={tableWrap} data-testid="admin-recupera-table">
              <table className={table}>
                <thead>
                  <tr>
                    <th className={th}>Cliente</th>
                    <th className={th}>Produto</th>
                    <th className={th}>Recuperados</th>
                    <th className={th}>Valor recuperado</th>
                    <th className={th}>Comissão</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={`${item.producerId}:${item.productId ?? '-'}`}>
                      <td className={td}>{item.producerEmail ?? item.producerId}</td>
                      <td className={td}>{item.productName ?? '-'}</td>
                      <td className={`${td} ${numeric}`}>{item.recoveredCount}</td>
                      <td className={`${td} ${numeric}`}>
                        <MoneyValue amount={item.recoveredRevenue} currency="BRL" size="sm" />
                      </td>
                      <td className={`${td} ${numeric}`}>
                        <MoneyValue amount={item.commission} currency="BRL" size="sm" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <LoadMore
              shown={items.length}
              hasMore={breakdown.hasNextPage}
              loading={breakdown.isFetchingNextPage}
              onClick={() => {
                void breakdown.fetchNextPage()
              }}
            />
          </>
        )}
      </Card>
    </div>
  )
}
